export interface ReadingEvidenceChoice {
  readonly id: string;
  readonly text: string;
}

export interface ReadingEvidenceDetail extends ReadingEvidenceChoice {
  readonly pageIndex: number;
}

export interface ReadingEvidenceConfig {
  readonly id: string;
  readonly skillId: string;
  readonly title: string;
  readonly pages: readonly string[];
  readonly question: string;
  readonly claimChoices: readonly ReadingEvidenceChoice[];
  readonly evidenceDetails: readonly ReadingEvidenceDetail[];
  readonly correctClaimId: string;
  readonly requiredEvidenceIds: readonly string[];
  readonly hints: readonly string[];
  readonly completionWorldEvent: string;
  readonly successMessage: string;
}

export function validateReadingEvidenceConfig<T extends ReadingEvidenceConfig>(config: T) {
  if (config.pages.length < 2) throw new RangeError(`${config.id} requires at least two readable pages.`);
  if (config.claimChoices.length < 2 || config.evidenceDetails.length < 3) {
    throw new RangeError(`${config.id} requires meaningful claim and evidence choices.`);
  }
  const claimIds = config.claimChoices.map(choice => choice.id);
  const detailIds = config.evidenceDetails.map(detail => detail.id);
  if (new Set(claimIds).size !== claimIds.length || new Set(detailIds).size !== detailIds.length) {
    throw new Error(`${config.id} contains duplicate choice identifiers.`);
  }
  if (!claimIds.includes(config.correctClaimId)) throw new Error(`${config.id} has no matching correct claim.`);
  if (config.requiredEvidenceIds.length < 2
    || config.requiredEvidenceIds.some(id => !detailIds.includes(id))) {
    throw new Error(`${config.id} requires at least two valid evidence details.`);
  }
  if (config.evidenceDetails.some(detail => detail.pageIndex < 0 || detail.pageIndex >= config.pages.length)) {
    throw new RangeError(`${config.id} evidence points to an invalid page.`);
  }
  if (![config.id, config.skillId, config.title, config.question, config.completionWorldEvent]
    .every(value => value.trim())) throw new RangeError('Reading evidence identifiers and copy must not be empty.');
  return config;
}
